import React, { useState } from "react";
import { X, Loader2, Users2, Lock } from "lucide-react";
import toast from "react-hot-toast";
import { patchAttendee } from "../../../api/inspection";

/**
 * Edit an inspection attendee's details from the property's Attendees tab.
 *
 * Patches name/email/phone plus the qualification fields the agent captures on
 * the register (interest level, budget, timeline). On success calls
 * onSaved(updatedAttendee) so the parent can update its list in place.
 *
 * Deliberately does NOT edit free text. Feedback used to be an editable box
 * here, and on the register row it rendered looking exactly like a note - so
 * opening Edit and typing over it silently replaced whatever the previous agent
 * had written (Christian Gravias, 2026-08-19: "I shouldnt be able to edit & over
 * write someones note"). Buyer notes are one append-only thread now, so the
 * existing feedback shows read-only here and the only way to add to it is the
 * Notes button on the buyer's row.
 *
 * Self-contained overlay (no shared UI primitives) so both the My Listings and
 * Collings Properties detail pages can reuse it regardless of their import roots.
 */
const INTEREST_OPTIONS = [
  { value: "", label: "—" },
  { value: "hot", label: "Hot" },
  { value: "warm", label: "Warm" },
  { value: "cold", label: "Cold" },
  { value: "none", label: "No Interest" },
  { value: "low", label: "Low Interest" },
  { value: "medium", label: "Medium Interest" },
  { value: "high", label: "High Interest" },
  { value: "very_high", label: "Very High — Ready to Offer" },
];

export default function EditAttendeeModal({ attendee, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: attendee?.name || "",
    email: attendee?.email || "",
    phone: attendee?.phone || "",
    interest_level: attendee?.interest_level || "",
    budget_range: attendee?.budget_range || "",
    timeline: attendee?.timeline || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Please enter a name.");
      return;
    }
    setSaving(true);
    try {
      const res = await patchAttendee(attendee.id, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        interest_level: form.interest_level,
        budget_range: form.budget_range.trim(),
        timeline: form.timeline.trim(),
      });
      toast.success("Attendee updated.");
      onSaved?.({ ...attendee, ...form, ...(res?.data || {}) });
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update attendee.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#55C5D0]";

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[92vh] flex flex-col my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <Users2 className="w-4 h-4 text-[#55C5D0]" />
            <h2 className="text-base font-semibold text-gray-900">Edit Attendee</h2>
          </div>
          <button onClick={onClose} aria-label="Close" className="-mr-1 p-1 text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div>
            <label className="text-xs font-bold text-[#374151] block mb-1">Name *</label>
            <input value={form.name} onChange={set("name")} className={inputCls} autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-[#374151] block mb-1">Email</label>
              <input type="email" value={form.email} onChange={set("email")} className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-bold text-[#374151] block mb-1">Phone</label>
              <input value={form.phone} onChange={set("phone")} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-[#374151] block mb-1">Interest level</label>
              <select value={form.interest_level} onChange={set("interest_level")} className={inputCls}>
                {INTEREST_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-[#374151] block mb-1">Budget</label>
              <input value={form.budget_range} onChange={set("budget_range")} placeholder="e.g. $800k–$900k" className={inputCls} />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-[#374151] block mb-1">Timeline</label>
            <input value={form.timeline} onChange={set("timeline")} placeholder="e.g. Within 3 months" className={inputCls} />
          </div>
          {/* Read-only: the buyer's stated feedback, folded into their note
              thread. Editing it here used to overwrite the previous agent's
              text, so there is no longer a box to type in. */}
          {(attendee?.feedback || "").trim() && (
            <div>
              <label className="text-xs font-bold text-[#374151] block mb-1">Buyer feedback</label>
              <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 whitespace-pre-wrap break-words">
                {attendee.feedback}
              </p>
            </div>
          )}
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
            <Lock className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-amber-800">
              Notes can't be edited or overwritten. To add to this buyer's notes, use{" "}
              <span className="font-semibold">Notes</span> on their row - it is saved with your name
              and the time, alongside every earlier note.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t bg-slate-50 rounded-b-2xl">
          <button type="button" onClick={onClose} disabled={saving}
            className="text-xs px-3 py-2 border rounded-md bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-50">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="text-xs flex items-center gap-1 px-4 py-2 bg-[#55C5D0] hover:bg-[#47b1bb] disabled:opacity-50 text-white rounded-md font-bold uppercase tracking-wider">
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
